import { useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/blocks";
import { SERVICE_DETAIL } from "@/data/content";
import { useUI } from "@/context/UIContext";

export default function ServiceGeneric() {
  const { slug } = useParams();
  const { openLead } = useUI();
  const data = SERVICE_DETAIL[slug];
  if (!data) return <Navigate to="/services" replace />;

  return (
    <div data-testid="service-generic-page">
      <Seo title={`${data.name} | Horsepower Garage`} description={data.intro}
        canonical={`https://www.horsepowergarage.fr/services/${slug}`} />
      <PageHero eyebrow={data.eyebrow} title={data.name} subtitle={data.intro} image={data.image} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <ul className="space-y-4">
              {data.points.map((p) => (
                <li key={p} className="flex items-start gap-3 hp-card p-4">
                  <Check className="text-hp-yellow shrink-0 mt-0.5" size={20} />
                  <span className="text-neutral-200">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="hp-card p-8">
              <h3 className="font-display font-bold uppercase tracking-tight text-2xl">Intéressé par ce service ?</h3>
              <p className="text-neutral-400 mt-3">Demandez un devis ou une prise de rendez-vous dans le centre de votre choix.</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button onClick={() => openLead({ request_type: "devis", service: data.name })} className="hp-cta px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2" data-testid="service-devis">Demander un devis <ArrowRight size={16} /></button>
                <button onClick={() => openLead({ request_type: "rdv", service: data.name })} className="hp-ghost px-6 py-3.5 text-sm">Prendre rendez-vous</button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      {data.gallery && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="service-gallery">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {data.gallery.map((src, i) => (
              <Reveal key={src} delay={i * 0.06}>
                <div className="hp-card overflow-hidden aspect-[3/2] group"><img src={src} alt={`${data.name} — Horsepower Garage`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /></div>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      {data.steps && (
        <section className="section-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
            <p className="text-hp-gold text-xs tracking-[0.3em] uppercase font-bold">Notre méthode</p>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-hp-dark mt-3">Le déroulé de l'intervention</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {data.steps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <div className="hp-card-light p-6 h-full" data-testid={`service-step-${s.n}`}>
                    <div className="font-display text-5xl text-hp-gold">{s.n}</div>
                    <h3 className="font-display font-bold uppercase tracking-tight text-xl text-hp-dark mt-3">{s.t}</h3>
                    <p className="text-hp-dark/60 text-sm mt-2">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            {data.note && <p className="mt-8 text-sm text-hp-dark/70 border-l-2 border-hp-gold pl-4" data-testid="service-note">{data.note}</p>}
          </div>
        </section>
      )}
      {data.why && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <p className="text-hp-yellow text-xs tracking-[0.3em] uppercase font-bold">Les bénéfices</p>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-3">Pourquoi faire réviser votre boîte ?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {data.why.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.08}>
                <div className="hp-card p-6 h-full border-t-2 border-hp-yellow">
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg">{w.t}</h3>
                  <p className="text-neutral-400 text-sm mt-2">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      <CTABand />
    </div>
  );
}
